import { colors, zIndex } from '@ardoq/design-tokens';
import { getDropdownMenuPortalTarget } from '@ardoq/dropdown-menu';
import { FlexBox } from '@ardoq/layout';
import { WithPopoverPlacement, WithPopover } from '@ardoq/popovers';
import { useRef, useState } from 'react';
import type * as React from 'react';
import {
  Multiselect,
  Select,
  type SelectOption,
  type SelectOptionsOrGroups,
} from '@ardoq/select';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { header4 } from '@ardoq/typography';
import type { SourceSelectComponents } from 'tabview/componentMatrix/componentMatrix2/ComponentMatrix2AxisDropdownSelectComponents';
import { AxisSortOrder, AxisSourceOption, AxisSourceValue } from './types';
import { TextInput } from '@ardoq/forms';
import { AxisHeaderTextInputProps, AxisHeaderInput } from './AxisHeaderInput';

export type SourceSelectProps = {
  'aria-label': string;
  dataTestId: string;
  placeholder: string;
  value: AxisSourceOption | undefined;
  options: SelectOptionsOrGroups<AxisSourceValue>;
  getOptionValue: (option: AxisSourceOption) => string;
  onValueChange: (value: AxisSourceValue | null) => void;
  filterOption: (
    option: { data: AxisSourceOption },
    inputValue: string
  ) => boolean;
  menuIsOpen: boolean;
  onMenuOpen: VoidFunction;
  onMenuClose: VoidFunction;
  components: SourceSelectComponents;
  autoFocus: true;
};

export type SortingSelectProps = {
  'aria-label': string;
  dataTestId: string;
  placeholder: string;
  value: AxisSortOrder | undefined;
  options: SelectOption<AxisSortOrder>[];
  onValueChange: (value: AxisSortOrder | null) => void;
  isDisabled: boolean;
};

export type AxisDropdownTexts = {
  sortingLabel: string;
  sortingPopoverContent: string | null;
  axisHeaderLabel: string;
  axisHeaderPopoverContent: string | null;
  axisHeaderHelperText: string;
  additionalFieldsLabel: string;
};

export type AxisHeaderInputProps = {
  inputProps: AxisHeaderTextInputProps;
  persistedValue: string | null;
  defaultValue: string;
  onPersist: (value: string | null) => void;
};

export type AdditionalFieldsSelectProps = {
  'aria-label': string;
  placeholder: string;
  options: SelectOptionsOrGroups<string>;
  value: SelectOption<string>[];
  onValueChange: (fieldNames: string[] | null) => void;
};

export type ComponentMatrix2AxisDropdownContentViewProps = {
  label: string;
  sourceSelect: SourceSelectProps;
  sortingSelect: SortingSelectProps;
  axisHeaderInput: AxisHeaderInputProps;
  texts: AxisDropdownTexts;
  additionalFieldsSelect?: AdditionalFieldsSelectProps;
};

const Label = styled.span<{ $isMuted?: boolean }>`
  ${header4};
  color: ${({ $isMuted }) => ($isMuted ? colors.textDisabled : 'inherit')};
`;

const portalHostStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  zIndex: zIndex.MODAL_BACKDROP + 1,
};

/**
 * The source/sorting/fields menus portal out of the panel (to the dropdown menu
 * portal target — `document.body` or the current fullscreen element), so a click
 * on an option is not a DOM descendant of the view-settings side panel. When that
 * panel guards itself with `useOnClickOutside`, the option click reads as an
 * outside click and closes the whole panel (ARD-33212). Selection is applied by
 * the option's own handler first; stopping the click here (bubble phase) keeps
 * it from reaching the document-level outside-click listener.
 */
const stopClickPropagation = (event: React.MouseEvent) =>
  event.stopPropagation();

const useAxisDropdownView = (
  onSourceMenuClose: VoidFunction,
  isHeaderInputDisabled: boolean,
  isSortingDisabled: boolean
) => {
  const [menuPortalTarget, setMenuPortalTarget] =
    useState<HTMLDivElement | null>(null);

  const sourceSelectRef = useRef<React.ComponentRef<typeof Select> | null>(
    null
  );
  const sortingSelectRef = useRef<React.ComponentRef<typeof Select> | null>(
    null
  );
  const axisHeaderInputRef = useRef<React.ComponentRef<
    typeof TextInput
  > | null>(null);
  const additionalFieldsSelectRef = useRef<React.ComponentRef<
    typeof Multiselect
  > | null>(null);

  return {
    menuPortalTarget,
    setMenuPortalTarget,
    sourceSelectRef,
    sortingSelectRef,
    axisHeaderInputRef,
    additionalFieldsSelectRef,
    onSourceKeyDown: (e: React.KeyboardEvent) => {
      if (e.key !== 'Tab' || e.shiftKey) {
        return;
      }

      onSourceMenuClose();
      // Header and sorting disabled states currently share the same source-value
      // invariant, so we do not expect "header disabled + sorting enabled" here.
      if (!isHeaderInputDisabled) {
        e.preventDefault();
        axisHeaderInputRef.current?.focus();
        return;
      }

      if (!isSortingDisabled) {
        e.preventDefault();
        sortingSelectRef.current?.focus();
        return;
      }

      if (additionalFieldsSelectRef.current) {
        e.preventDefault();
        additionalFieldsSelectRef.current.focus();
      }
    },
    onAxisHeaderInputKeyDown: (e: React.KeyboardEvent) => {
      if (e.key !== 'Tab') {
        return;
      }

      if (e.shiftKey) {
        e.preventDefault();
        sourceSelectRef.current?.focus();
        return;
      }

      if (!isSortingDisabled) {
        e.preventDefault();
        sortingSelectRef.current?.focus();
        return;
      }

      if (additionalFieldsSelectRef.current) {
        e.preventDefault();
        additionalFieldsSelectRef.current.focus();
      }
    },
    onSortingKeyDown: (e: React.KeyboardEvent) => {
      if (e.key !== 'Tab') {
        return;
      }

      if (e.shiftKey) {
        e.preventDefault();
        if (!isHeaderInputDisabled) {
          axisHeaderInputRef.current?.focus();
        } else {
          sourceSelectRef.current?.focus();
        }
        return;
      }

      if (additionalFieldsSelectRef.current) {
        e.preventDefault();
        additionalFieldsSelectRef.current.focus();
      }
    },
    onAdditionalFieldsKeyDown: (e: React.KeyboardEvent) => {
      if (e.key !== 'Tab' || !e.shiftKey) {
        return;
      }

      if (!isSortingDisabled) {
        e.preventDefault();
        sortingSelectRef.current?.focus();
        return;
      }

      e.preventDefault();
      if (!isHeaderInputDisabled) {
        axisHeaderInputRef.current?.focus();
      } else {
        sourceSelectRef.current?.focus();
      }
    },
  };
};

const ComponentMatrix2AxisDropdownContentView = ({
  label,
  sourceSelect,
  sortingSelect,
  texts,
  axisHeaderInput,
  additionalFieldsSelect,
}: ComponentMatrix2AxisDropdownContentViewProps) => {
  const {
    menuPortalTarget,
    setMenuPortalTarget,
    sourceSelectRef,
    sortingSelectRef,
    axisHeaderInputRef,
    onSourceKeyDown,
    onSortingKeyDown,
    onAxisHeaderInputKeyDown,
    additionalFieldsSelectRef,
    onAdditionalFieldsKeyDown,
  } = useAxisDropdownView(
    sourceSelect.onMenuClose,
    axisHeaderInput.inputProps.isDisabled,
    sortingSelect.isDisabled
  );

  return (
    <>
      {createPortal(
        // Portal host is React-managed and mounted into an existing container
        // (`document.body` or the current fullscreen element), so it is removed
        // automatically on unmount; no manual cleanup is needed here.
        <div ref={setMenuPortalTarget} style={portalHostStyle} />,
        getDropdownMenuPortalTarget()
      )}

      <div onClick={stopClickPropagation}>
        <FlexBox
          flexDirection="column"
          gap="small"
          padding="small"
          width="full"
        >
          <Label>{label}</Label>
          <Select
            {...sourceSelect}
            ref={sourceSelectRef}
            menuPortalTarget={menuPortalTarget}
            onKeyDown={onSourceKeyDown}
          />
          <Label $isMuted={axisHeaderInput.inputProps.isDisabled}>
            {texts.axisHeaderLabel}
          </Label>
          <WithPopover
            content={texts.axisHeaderPopoverContent}
            preferredPlacement={WithPopoverPlacement.BOTTOM}
          >
            <div>
              <AxisHeaderInput
                inputProps={axisHeaderInput.inputProps}
                persistedValue={axisHeaderInput.persistedValue}
                defaultValue={axisHeaderInput.defaultValue}
                onPersist={axisHeaderInput.onPersist}
                helperText={texts.axisHeaderHelperText}
                inputRef={axisHeaderInputRef}
                onKeyDown={onAxisHeaderInputKeyDown}
              />
            </div>
          </WithPopover>
          <Label $isMuted>{texts.sortingLabel}</Label>
          <WithPopover
            content={texts.sortingPopoverContent}
            preferredPlacement={WithPopoverPlacement.BOTTOM}
          >
            <div>
              <Select
                {...sortingSelect}
                ref={sortingSelectRef}
                menuPortalTarget={menuPortalTarget}
                onKeyDown={onSortingKeyDown}
              />
            </div>
          </WithPopover>
          {additionalFieldsSelect && (
            <>
              <Label>{texts.additionalFieldsLabel}</Label>
              <Multiselect
                aria-label={additionalFieldsSelect['aria-label']}
                placeholder={additionalFieldsSelect.placeholder}
                options={additionalFieldsSelect.options}
                value={additionalFieldsSelect.value}
                onValueChange={additionalFieldsSelect.onValueChange}
                menuPortalTarget={menuPortalTarget}
                ref={additionalFieldsSelectRef}
                onKeyDown={onAdditionalFieldsKeyDown}
              />
            </>
          )}
        </FlexBox>
      </div>
    </>
  );
};

export { ComponentMatrix2AxisDropdownContentView };
